import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import {
	DateString,
	getCurrentDateString,
} from "seedgreen-shared/types/datestring.types";
import { useSeasons } from "seedgreen-shared/queries/season";
import { useCoolers } from "seedgreen-shared/queries/cooler";
import { useHarvestOverviewRanches } from "seedgreen-shared/queries/harvest-overview";

export const useBerriesHarvestStore = defineStore(
	"berriesHarvest",
	() => {
		// Filter state
		const organicStatus = ref<number | null>(null);
		const season = ref<number | null>(null);
		const date = ref<DateString>(getCurrentDateString());
		const coolers = ref<number[]>([]);

		// Query data
		const { seasons: seasonsQuery } = useSeasons();
		const { coolers: coolersQuery } = useCoolers();
		const { ranches: ranchesQuery } = useHarvestOverviewRanches();

		// Computed data
		const seasons = computed(() => seasonsQuery.value || []);
		const availableCoolers = computed(() => coolersQuery.value || []);
		const ranches = computed(() => ranchesQuery.value || []);

		// Auto-select latest season for first-time users
		watch(seasons, (newSeasons) => {
			if (
				newSeasons?.length &&
				season.value === null &&
				!localStorage.getItem("berries-harvest-filters")
			) {
				// Default to the season with the most recent start date
				const latest = [...newSeasons]
					.filter((s) => !!s?.start)
					.sort(
						(a, b) =>
							new Date(b.start as string).getTime() -
							new Date(a.start as string).getTime(),
					);
				season.value = (latest[0] ?? newSeasons[0])?.id ?? null;
			}
		});

		function resetFilters() {
			organicStatus.value = null;
			season.value = null;
			date.value = getCurrentDateString();
			coolers.value = [];
		}

		return {
			// Filter state
			organicStatus,
			season,
			date,
			coolers,

			// Data
			seasons,
			availableCoolers,
			ranches,

			// Actions
			resetFilters,
		};
	},
	{
		persist: {
			key: "berries-harvest-filters",
			pick: ["organicStatus", "season", "coolers"],
		},
	},
);
